import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { teams, ALL_POKEMON } from '../data/teams';
import { getTeamRecommendation, recomputeGamePlan } from '../data/selectionAdvisor';
import { loadHistory, saveMatch, computeLearnedAdjustments, clearHistory } from '../data/matchHistory';
import { enrichOpponent } from '../data/pokemonDefaults';
import { getBaseStats, getMegaStats, getMegaStatsByStone, isMegaStone, getMegaTypes, canMegaEvolve, getMegaAbility } from '../data/baseStats';
import { TypeBadge, TYPE_COLORS } from './TeamBrowser';
import { getSpriteUrl, getMegaSpriteUrl, getTypeIconUrl, getItemSpriteUrl, handleItemIconError } from '../data/sprites';
import MOVE_TYPES from '../data/moves';
import { estimateDamage, getEffectiveStats, getEffectiveSpeed } from '../data/damageCalc';
import MOVE_DATA from '../data/moveData';
import ABILITIES from '../data/abilities';
import { getDefaultAbility, getAbilityUsage } from '../data/pokemonMeta';
import VictoryPopup from './VictoryPopup';
import { useTeams } from '../auth/useTeams';
import { track } from '../analytics';
import './SelectionAdvisor.css';

const OPP_HISTORY_KEY = 'pokemon-champions-opp-history';

function enrichSavedTeam(t) {
  return {
    id: t.id,
    name: t.name,
    archetype: 'Custom',
    pokemon: (t.pokemon || []).map(p => {
      const enriched = { ...p, moves: p.moves || [], role: p.role || '' };
      if (!enriched.baseStats) enriched.baseStats = getBaseStats(enriched.name);
      if (enriched.item && isMegaStone(enriched.item) && !enriched.megaStats) {
        enriched.megaStats = getMegaStats(enriched.name);
      }
      return enriched;
    }),
    // Preserve the strategy fields so the advisor picker (which selects
    // straight out of `savedTeams`) and the freshTeam lookup both keep the
    // structured strategy. Stripping these here was the reason the advisor
    // ignored the strategy when a team was picked from its own UI.
    strategy: t.strategy || null,
    advisorStrategy: t.advisorStrategy || null,
  };
}

function loadOppHistory() {
  try { return JSON.parse(localStorage.getItem(OPP_HISTORY_KEY) || '[]'); } catch { return []; }
}

function saveOppHistory(list) {
  localStorage.setItem(OPP_HISTORY_KEY, JSON.stringify(list.slice(0, 20)));
}

// Compact relative time formatter for history entries ("5m ago", "3d ago", "Apr 14").
function formatRelTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Apply mega-evolved stats/types/ability to a Pokemon when active.
// Used by damage calcs so toggling Mega on the field changes effectiveness.
// When NOT mega'd we must strip `megaStats` — enrichSavedTeam attaches it to any
// Pokemon holding a mega stone, and getEffectiveStats prefers megaStats over
// baseStats, so leaving it in place would leak mega stats into the pre-mega calc.
function withMega(p, isMega) {
  if (!p) return p;
  if (isMega) {
    // canMegaEvolve gates the toggle; the Mega Stone is optional here because
    // opponent Pokemon usually carry default items (Focus Sash, etc.) and we
    // still want a manual Mega toggle to apply the type/stat/ability change.
    // When the stone is present it disambiguates Charizard X vs Y; otherwise
    // the downstream getters fall back to the first available Mega form.
    if (!canMegaEvolve(p)) return p;
    const stone = isMegaStone(p.item) ? p.item : null;
    const megaStats = getMegaStatsByStone(p.name, stone) || getMegaStats(p.name);
    const megaTypes = getMegaTypes(p.name, stone);
    const megaAbility = getMegaAbility(p.name, stone);
    return {
      ...p,
      baseStats: megaStats || p.baseStats,
      megaStats: megaStats || p.megaStats,
      types: megaTypes || p.types,
      ability: megaAbility || p.ability,
    };
  }
  // Not mega — ensure pre-mega stats are used even if the team attached megaStats.
  if (p.megaStats) {
    const { megaStats, ...rest } = p;
    return rest;
  }
  return p;
}

// ============================================================
// StageControls — interactive +/- chips for each stat (-6..+6)
// Bound to a single Pokemon name; damage calcs read from stageBoosts[name].
// ============================================================
const STAGE_STATS = [
  { key: 'atk', label: 'Atk' },
  { key: 'def', label: 'Def' },
  { key: 'spa', label: 'SpA' },
  { key: 'spd', label: 'SpD' },
  { key: 'spe', label: 'Spe' },
];

function StageControls({ name, boosts, onAdjust, onReset }) {
  const hasAny = STAGE_STATS.some(s => (boosts[s.key] || 0) !== 0);
  return (
    <div className="ba-stage-controls">
      {STAGE_STATS.map(s => {
        const v = boosts[s.key] || 0;
        const cls = v > 0 ? 'pos' : v < 0 ? 'neg' : '';
        return (
          <div key={s.key} className={`ba-stage-chip ${cls}`}>
            <button
              type="button"
              className="ba-stage-btn"
              aria-label={`Decrease ${s.label}`}
              onClick={() => onAdjust(name, s.key, -1)}
              disabled={v <= -6}
            >−</button>
            <span className="ba-stage-label">{s.label}</span>
            <span className="ba-stage-val">{v > 0 ? `+${v}` : v}</span>
            <button
              type="button"
              className="ba-stage-btn"
              aria-label={`Increase ${s.label}`}
              onClick={() => onAdjust(name, s.key, +1)}
              disabled={v >= 6}
            >+</button>
          </div>
        );
      })}
      <button
        type="button"
        className="ba-stage-reset"
        onClick={() => hasAny && onReset(name)}
        aria-label="Reset all stat stages"
        title="Reset stat stages"
        aria-hidden={!hasAny}
        tabIndex={hasAny ? 0 : -1}
        style={hasAny ? undefined : { visibility: 'hidden' }}
      >↺</button>
    </div>
  );
}

// ============================================================
// Component
// ============================================================
function SelectionAdvisor({ preselectedTeam, onGoToMyTeams }) {
  const [yourTeam, setYourTeam] = useState(preselectedTeam || null);
  const [opponentPokemon, setOpponentPokemon] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [battleFormat, setBattleFormat] = useState('doubles');
  const [activeTab, setActiveTab] = useState('overview');

  // Match recording
  const [showRecorder, setShowRecorder] = useState(false);
  const [actualBrought, setActualBrought] = useState([]);
  const [actualLeads, setActualLeads] = useState([]);

  // Game plan — interactive active Pokemon for both sides
  const [activeYours, setActiveYours] = useState([]);
  const [activeTheirs, setActiveTheirs] = useState([]);
  // Keyed by side-prefixed name ("you:Dragonite" vs "opp:Dragonite") so shared
  // species don't mega-toggle together.
  const [megaEvolved, setMegaEvolved] = useState({});
  const megaKey = (name, isOpponent) => `${isOpponent ? 'opp' : 'you'}:${name}`;
  const isMegaYou = (name) => !!megaEvolved[megaKey(name, false)];
  const isMegaOpp = (name) => !!megaEvolved[megaKey(name, true)];
  // Stat-stage adjustments per Pokemon: { [name]: { atk, def, spa, spd, spe } }, each -6..+6
  const [stageBoosts, setStageBoosts] = useState({});

  const adjustBoost = (name, stat, delta) => {
    setStageBoosts(prev => {
      const current = prev[name] || {};
      const next = Math.max(-6, Math.min(6, (current[stat] || 0) + delta));
      return { ...prev, [name]: { ...current, [stat]: next } };
    });
  };

  const resetBoosts = (name) => {
    setStageBoosts(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Battle mode
  const [battleMode, setBattleMode] = useState(false);
  // Keyed by side-prefixed name ("you:Dragonite" vs "opp:Dragonite") so shared
  // species on both teams don't toggle together.
  const [faintedMons, setFaintedMons] = useState({});
  const faintKey = (name, isOpponent) => `${isOpponent ? 'opp' : 'you'}:${name}`;
  const isFaintedYou = (name) => !!faintedMons[faintKey(name, false)];
  const isFaintedOpp = (name) => !!faintedMons[faintKey(name, true)];
  const [confirmedOppLeads, setConfirmedOppLeads] = useState([]);
  const [confirmedOppBrought, setConfirmedOppBrought] = useState([]);
  const [matchResult, setMatchResult] = useState(null); // 'win' | 'loss' | 'forfeit-win' | 'forfeit-loss' | null
  const [endGamePrompt, setEndGamePrompt] = useState(null); // null | 'win' | 'loss' — shows validation

  const toggleFainted = (name, isOpponent = false) => {
    const key = faintKey(name, isOpponent);
    const wasFainted = !!faintedMons[key];
    setFaintedMons(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
    // Marking an opponent fainted implies they were brought — auto-record it
    // so the "Brought" tally stays consistent and the save-match flow doesn't
    // get blocked on the end-of-battle validation.
    if (isOpponent && !wasFainted) {
      setConfirmedOppBrought(prev => {
        if (prev.includes(name)) return prev;
        if (prev.length >= selectCount) return prev;
        return [...prev, name];
      });
    }
  };

  const toggleMega = (name, isOpponent = false) => {
    const key = megaKey(name, isOpponent);
    setMegaEvolved(prev => {
      const next = { ...prev };
      const becomingMega = !next[key];
      if (next[key]) delete next[key];
      else next[key] = true;
      track('mega_toggled', { pokemon: name, side: isOpponent ? 'opp' : 'you', enabled: becomingMega });
      return next;
    });
  };

  const toggleOppConfirmedLead = (name) => {
    setConfirmedOppLeads(prev => {
      if (prev.includes(name)) return prev.filter(n => n !== name);
      const max = battleFormat === 'doubles' ? 2 : 1;
      if (prev.length >= max) return [...prev.slice(1), name];
      return [...prev, name];
    });
    // Auto-add to brought (respect the format's bring cap)
    setConfirmedOppBrought(prev => {
      if (prev.includes(name)) return prev;
      if (prev.length >= selectCount) return prev;
      return [...prev, name];
    });
  };

  const toggleOppConfirmedBrought = (name) => {
    setConfirmedOppBrought(prev => {
      if (prev.includes(name)) {
        setConfirmedOppLeads(l => l.filter(n => n !== name));
        return prev.filter(n => n !== name);
      }
      if (prev.length >= selectCount) return prev;
      return [...prev, name];
    });
  };

  const startBattle = () => {
    setBattleMode(true);
    setFaintedMons({});
    setConfirmedOppLeads([]);
    setConfirmedOppBrought([]);
    setMatchResult(null);
    setEndGamePrompt(null);
    // Set active Pokemon from lead suggestion
    const maxActive = battleFormat === 'doubles' ? 2 : 1;
    setActiveYours((recommendation?.leadSuggestion?.leads || []).slice(0, maxActive));
    const oppLeadPreds = recommendation?.opponentPrediction?.predictedLeads?.[0]?.leads || [];
    setActiveTheirs(oppLeadPreds.slice(0, maxActive));
    track('battle_started', {
      format: battleFormat,
      team: yourTeam?.name,
      opp_size: opponentPokemon.length,
    });
  };

  const tryEndBattle = (result) => {
    const yourFaintedCount = (yourTeam?.pokemon || []).filter(p => isFaintedYou(p.name)).length;
    const oppFaintedCount = opponentPokemon.filter(p => isFaintedOpp(p.name)).length;

    // A loss only requires that enough of YOUR Pokemon fainted. Don't block on
    // opponent tracking — the opponent may have won without us seeing all of
    // their picks.
    if (result === 'loss') {
      if (yourFaintedCount < selectCount) {
        setEndGamePrompt(result);
        return;
      }
      finishBattle(result);
      return;
    }

    // Win: require that we KO'd enough opponent Pokemon. Leads/brought beyond
    // that is nice-to-have for prediction data but not required.
    if (oppFaintedCount < selectCount) {
      setEndGamePrompt(result);
      return;
    }
    finishBattle(result);
  };

  const finishBattle = (result) => {
    setMatchResult(result);
    setEndGamePrompt(null);

    // Build rich match record for analytics
    const yourPokemonNames = yourTeam?.pokemon?.map(p => p.name) || [];
    const yourBrought = recommendation?.selected?.map(s => s.name) || [];
    const yourLeads = recommendation?.leadSuggestion?.leads || [];
    const yourFainted = yourPokemonNames.filter(n => isFaintedYou(n));
    const oppFainted = opponentPokemon.map(p => p.name).filter(n => isFaintedOpp(n));

    track('match_logged', {
      result,
      format: battleFormat,
      your_fainted: yourFainted.length,
      opp_fainted: oppFainted.length,
    });

    saveMatch({
      // Core IDs
      yourTeamName: yourTeam?.name,
      yourTeamId: yourTeam?.id,
      format: battleFormat,
      result, // 'win' | 'loss' | 'forfeit-win' | 'forfeit-loss'

      // Your side
      yourTeam: yourPokemonNames.map(name => {
        const p = yourTeam?.pokemon?.find(pk => pk.name === name);
        return { name, types: p?.types || [] };
      }),
      yourBrought,
      yourLeads,
      yourFainted,

      // Opponent side
      opponentTeam: opponentPokemon.map(p => ({
        name: p.name,
        types: p.types,
        ability: p.ability || null,
      })),
      opponentBrought: confirmedOppBrought,
      opponentLeads: confirmedOppLeads,
      opponentFainted: oppFainted,

      // Matchup context
      archetype: recommendation?.archetypeAnalysis?.archetype || null,
    });
    setHistoryVersion(v => v + 1);
  };

  // History
  const [historyVersion, setHistoryVersion] = useState(0);
  const history = useMemo(() => loadHistory(), [historyVersion]);
  const learnedAdjustments = useMemo(() => computeLearnedAdjustments(history), [history]);

  // Victory popup — shown when the user wins a battle
  const [showVictory, setShowVictory] = useState(false);
  useEffect(() => {
    if (matchResult === 'win' || matchResult === 'forfeit-win') {
      setShowVictory(true);
    }
  }, [matchResult]);

  const battleStats = useMemo(() => {
    const matches = history.filter(m => m.result);
    const wins = matches.filter(m => m.result === 'win' || m.result === 'forfeit-win').length;
    const losses = matches.filter(m => m.result === 'loss' || m.result === 'forfeit-loss').length;
    const total = wins + losses;
    const rate = total > 0 ? Math.round((wins / total) * 100) : 0;
    let streak = 0;
    for (let i = matches.length - 1; i >= 0; i--) {
      const r = matches[i].result;
      if (r === 'win' || r === 'forfeit-win') streak++;
      else break;
    }
    return { wins, losses, rate, streak };
  }, [history]);

  const handleNewBattle = () => {
    setShowVictory(false);
    setRecommendation(null);
    setBattleMode(false);
    setMatchResult(null);
    setFaintedMons({});
    setActiveYours([]);
    setActiveTheirs([]);
    setConfirmedOppBrought([]);
    setConfirmedOppLeads([]);
    setEndGamePrompt(null);
    setOpponentPokemon([]);
    setMegaEvolved({});
    setStageBoosts({});
  };
  // Saved teams flow in live from Firestore via the auth-aware hook.
  const { teams: rawSavedTeams } = useTeams();
  const savedTeams = useMemo(
    () => rawSavedTeams.map(enrichSavedTeam),
    [rawSavedTeams]
  );

  // Opponent team history
  const [oppHistoryVer, setOppHistoryVer] = useState(0);
  const oppHistory = useMemo(() => loadOppHistory(), [oppHistoryVer]);

  // Match history panel controls (setup phase)
  const [historyTeamFilter, setHistoryTeamFilter] = useState(true);  // default: filter to current team
  const [showAllHistory, setShowAllHistory] = useState(false);

  const selectCount = battleFormat === 'doubles' ? 4 : 3;

  const filteredPokemon = (() => {
    const q = searchQuery.trim().toLowerCase();
    const notSelected = p => !opponentPokemon.some(op => op.name === p.name);
    if (q.length >= 2) {
      return ALL_POKEMON.filter(p => p.name.toLowerCase().includes(q) && notSelected(p)).slice(0, 8);
    }
    // Empty/short query while focused: show first matches alphabetically so the
    // dropdown appears on click, giving users something to scan through.
    if (searchFocused) {
      return ALL_POKEMON
        .filter(notSelected)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 12);
    }
    return [];
  })();

  const addOpponentPokemon = (pokemon) => {
    if (opponentPokemon.length < 6) {
      setOpponentPokemon(prev => [...prev, enrichOpponent(pokemon, battleFormat)]);
      setSearchQuery('');
      setRecommendation(null);
    }
  };

  const removeOpponentPokemon = (name) => {
    setOpponentPokemon(prev => prev.filter(p => p.name !== name));
    setRecommendation(null);
  };

  const loadOppTeam = (team) => {
    setOpponentPokemon(team.map(p => enrichOpponent(p, battleFormat)));
    setRecommendation(null);
  };

  const deleteOppHistory = (idx) => {
    const next = oppHistory.filter((_, i) => i !== idx);
    saveOppHistory(next);
    setOppHistoryVer(v => v + 1);
  };

  const analyze = () => {
    if (!yourTeam || opponentPokemon.length === 0) return;
    // Pick up the latest version of the team if it's still in the saved list
    // (Firestore subscription keeps `savedTeams` fresh). Preset teams from
    // teams.js carry their own advisorStrategy and must not be shadowed by a
    // user-saved team that happens to share a name.
    //
    // Critical: the savedTeams snapshot can lag a save by a second or two,
    // so if `yourTeam` was just handed off from MyTeams with a populated
    // strategy, we must not let a stale freshTeam (which still lacks the
    // strategy) override it. Use freshTeam for the team body, but always
    // prefer yourTeam.strategy when it's present and freshTeam's isn't.
    const isPreset = !!yourTeam.advisorStrategy || (typeof yourTeam.id === 'string' && /^[a-z]/.test(yourTeam.id));
    const freshTeam = isPreset
      ? null
      : (savedTeams.find(t => t.id === yourTeam.id) || savedTeams.find(t => t.name === yourTeam.name));
    const teamToUse = freshTeam
      ? {
          ...freshTeam,
          // Prefer the in-process strategy if freshTeam's is missing/stale.
          strategy: freshTeam.strategy || yourTeam.strategy,
          advisorStrategy: freshTeam.advisorStrategy || yourTeam.advisorStrategy,
        }
      : yourTeam;
    // eslint-disable-next-line no-console
    console.log('[advisor:analyze] team handoff', {
      yourTeam: { id: yourTeam.id, name: yourTeam.name, hasAdvisorStrategy: !!yourTeam.advisorStrategy, strategyType: typeof yourTeam.strategy, strategyKeys: yourTeam.strategy && typeof yourTeam.strategy === 'object' ? Object.keys(yourTeam.strategy) : null },
      isPreset,
      freshTeam: freshTeam ? { id: freshTeam.id, name: freshTeam.name, hasAdvisorStrategy: !!freshTeam.advisorStrategy, strategyType: typeof freshTeam.strategy, strategyKeys: freshTeam.strategy && typeof freshTeam.strategy === 'object' ? Object.keys(freshTeam.strategy) : null } : null,
      teamToUse: { id: teamToUse.id, name: teamToUse.name, hasAdvisorStrategy: !!teamToUse.advisorStrategy, strategyType: typeof teamToUse.strategy },
      stratPassedToAdvisor: teamToUse.advisorStrategy || teamToUse.strategy,
      savedTeamsCount: savedTeams.length,
      savedTeamMatchById: !!savedTeams.find(t => t.id === yourTeam.id),
      savedTeamMatchByName: !!savedTeams.find(t => t.name === yourTeam.name),
    });
    // Always update state so sidebar and game plan use latest data
    setYourTeam(teamToUse);

    // Enrich your own team with baseStats if missing
    const enrichedYours = teamToUse.pokemon.map(p => {
      const e = { ...p };
      if (!e.baseStats) e.baseStats = getBaseStats(e.name);
      if (e.item && isMegaStone(e.item) && !e.megaStats) e.megaStats = getMegaStats(e.name);
      return e;
    });
    // Enrich opponents with predicted stats/moves/abilities
    const enrichedOpp = opponentPokemon.map(p => enrichOpponent(p, battleFormat));
    const rec = getTeamRecommendation(enrichedYours, enrichedOpp, selectCount, learnedAdjustments, teamToUse.advisorStrategy || teamToUse.strategy, battleFormat);
    setRecommendation(rec);
    setShowRecorder(false);
    setActualBrought([]);
    setActualLeads([]);
    setActiveTab('overview');
    setMegaEvolved({});
    setStageBoosts({});

    // Initialize active Pokemon from recommendations
    const maxActive = selectCount === 4 ? 2 : 1;
    setActiveYours((rec.leadSuggestion?.leads || []).slice(0, maxActive));
    const oppLeadNames = rec.opponentPrediction?.predictedLeads?.[0]?.leads || [];
    setActiveTheirs(oppLeadNames.length > 0 ? oppLeadNames.slice(0, maxActive)
      : (rec.opponentPrediction?.predictions || []).filter(p => p.likely).slice(0, maxActive).map(p => p.name));

    // Save opponent team to history
    const key = opponentPokemon.map(p => p.name).sort().join(',');
    const exists = oppHistory.some(h => h.map(p => p.name).sort().join(',') === key);
    if (!exists) {
      const next = [opponentPokemon.map(p => ({ name: p.name, types: p.types })), ...oppHistory];
      saveOppHistory(next);
      setOppHistoryVer(v => v + 1);
    }

    // Jump to top so the freshly generated game plan is visible.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Match recording
  const toggleBrought = useCallback((name) => {
    setActualBrought(prev => {
      if (prev.includes(name)) { setActualLeads(l => l.filter(n => n !== name)); return prev.filter(n => n !== name); }
      if (prev.length >= selectCount) return prev;
      return [...prev, name];
    });
  }, [selectCount]);

  const toggleLead = useCallback((name) => {
    if (!actualBrought.includes(name)) return;
    const max = battleFormat === 'doubles' ? 2 : 1;
    setActualLeads(prev => {
      if (prev.includes(name)) return prev.filter(n => n !== name);
      if (prev.length >= max) return prev;
      return [...prev, name];
    });
  }, [actualBrought, battleFormat]);

  const recordMatch = () => {
    if (actualBrought.length === 0) return;
    saveMatch({ yourTeam: yourTeam?.name, opponentTeam: opponentPokemon.map(p => ({ name: p.name, types: p.types })), opponentBrought: actualBrought, opponentLeads: actualLeads, format: battleFormat });
    setHistoryVersion(v => v + 1);
    setShowRecorder(false);
    setActualBrought([]);
    setActualLeads([]);
  };

  const predictions = recommendation?.opponentPrediction?.predictions;
  const predictedLeads = recommendation?.opponentPrediction?.predictedLeads;
  const arch = recommendation?.archetypeAnalysis;
  const wincon = recommendation?.winConditions;
  const dmg = recommendation?.damageEstimates;

  // Recompute game plan live when active Pokemon change
  const { commentary, dt, battlePlan } = useMemo(() => {
    if (!recommendation) return { commentary: null, dt: null, battlePlan: null };

    const minActive = selectCount === 4 ? 2 : 1;
    if (activeYours.length >= minActive && activeTheirs.length >= minActive) {
      const enrichedOpp = opponentPokemon.map(p => enrichOpponent(p, battleFormat));

      const yourActivePokemon = activeYours.map(n => {
        const p = yourTeam?.pokemon?.find(pk => pk.name === n);
        if (!p) return null;
        const e = { ...p };
        if (!e.baseStats) e.baseStats = getBaseStats(e.name);
        if (e.item && isMegaStone(e.item) && !e.megaStats) e.megaStats = getMegaStats(e.name);
        // Apply mega evolution type/stat changes
        if (isMegaYou(e.name)) {
          const mStats = getMegaStats(e.name);
          if (mStats) e.baseStats = mStats;
          const mTypes = getMegaTypes(e.name, e.item);
          if (mTypes) e.types = mTypes;
        }
        return e;
      }).filter(Boolean);

      const theirActivePokemon = activeTheirs.map(n => {
        const p = enrichedOpp.find(pk => pk.name === n);
        if (!p) return null;
        const e = { ...p };
        if (isMegaOpp(e.name)) {
          const mStats = getMegaStats(e.name);
          if (mStats) e.baseStats = mStats;
          const mTypes = getMegaTypes(e.name, e.item);
          if (mTypes) e.types = mTypes;
        }
        return e;
      }).filter(Boolean);

      if (yourActivePokemon.length >= minActive && theirActivePokemon.length >= minActive) {
        const result = recomputeGamePlan(
          yourActivePokemon, theirActivePokemon,
          enrichedOpp, yourTeam?.pokemon || [], selectCount, yourTeam?.advisorStrategy || yourTeam?.strategy, battleFormat
        );
        return { commentary: result.commentary, dt: result.decisionTree, battlePlan: result.battlePlan };
      }
    }

    return { commentary: recommendation.commentary, dt: recommendation.decisionTree, battlePlan: recommendation.battlePlan };
  }, [recommendation, activeYours, activeTheirs, opponentPokemon, yourTeam, selectCount, megaEvolved]);

  // Top 10 popular Pokemon for quick-select
  const popularPokemon = ['Incineroar','Garchomp','Dragapult','Kingambit','Whimsicott','Farigiraf','Milotic','Tyranitar','Torkoal','Pelipper']
    .map(name => ALL_POKEMON.find(p => p.name === name)).filter(Boolean);

  return (
    <div className={`ba${!recommendation ? ' ba-setup-mode' : ''}`}>
      {showVictory && (
        <VictoryPopup
          result={matchResult}
          stats={battleStats}
          onNewBattle={handleNewBattle}
          onClose={() => setShowVictory(false)}
        />
      )}
      {/* ---- SETUP PHASE (full screen) ---- */}
      {!recommendation ? (
        <div className="ba-setup">
          <div className="ba-setup-header">
            <h2>Battle Advisor</h2>
            <p className="subtitle">Select your team and your opponent's Pokemon to get a full matchup analysis.</p>
            {history.length > 0 && (
              <div className="ba-history-badge">
                <span>{history.length} match{history.length !== 1 ? 'es' : ''} learned</span>
                <button onClick={() => { clearHistory(); setHistoryVersion(v => v + 1); }}>Clear</button>
              </div>
            )}
          </div>

          <div className="ba-setup-columns">
            {/* Left: Your Team */}
            <div className="ba-setup-panel">
              <div className="ba-setup-panel-label">Your Team</div>
              {savedTeams.length === 0 && (
                <div className="ba-empty">
                  <p>No saved teams.</p>
                  <button type="button" className="ba-empty-btn" onClick={onGoToMyTeams}>
                    Create one in My Teams
                  </button>
                </div>
              )}
              <div className="ba-setup-team-grid">
                {savedTeams.map(t => (
                  <button key={t.id} className={`ba-setup-team-card${yourTeam?.id === t.id ? ' selected' : ''}`}
                    onClick={() => { setYourTeam(t); setRecommendation(null); }}>
                    <span className="ba-setup-team-name">{t.name}</span>
                    <div className="ba-setup-team-roster">
                      {t.pokemon.map(p => (
                        <img key={p.name} src={getSpriteUrl(p.name)} alt={p.name} title={p.name} className="ba-setup-roster-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      ))}
                    </div>
                  </button>
                ))}
              </div>

              {yourTeam && (
                <div className="ba-setup-your-preview">
                  {yourTeam.pokemon.map(p => (
                    <div key={p.name} className="ba-setup-preview-mon">
                      <img src={getSpriteUrl(p.name)} alt="" className="ba-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      <div>
                        <span className="ba-setup-preview-name">{p.name}</span>
                        <div style={{display:'flex', gap:'0.15rem'}}>
                          {p.types.map(t => <TypeBadge key={t} type={t} />)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Opponent Team */}
            <div className="ba-setup-panel">
              <div className="ba-setup-panel-label">Opponent Team</div>

              <div className="ba-format">
                <button className={battleFormat === 'doubles' ? 'on' : ''} onClick={() => { setBattleFormat('doubles'); setRecommendation(null); }}>Doubles (4)</button>
                <button className={battleFormat === 'singles' ? 'on' : ''} onClick={() => { setBattleFormat('singles'); setRecommendation(null); }}>Singles (3)</button>
              </div>

              {/* Recent opponents — max 3 */}
              {oppHistory.length > 0 && (
                <div className="ba-setup-recent">
                  <div className="ba-setup-recent-label">Recent opponents</div>
                  {oppHistory.slice(0, 3).map((team, i) => (
                    <button key={i} className="ba-setup-recent-btn" onClick={() => loadOppTeam(team)}>
                      {team.map(p => (
                        <img key={p.name} src={getSpriteUrl(p.name)} alt={p.name} title={p.name} className="ba-setup-roster-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      ))}
                    </button>
                  ))}
                </div>
              )}

              {/* Quick-select popular Pokemon */}
              {opponentPokemon.length < 6 && (
                <div className="ba-setup-popular">
                  <div className="ba-setup-recent-label">Popular picks</div>
                  <div className="ba-setup-popular-grid">
                    {popularPokemon.filter(p => !opponentPokemon.some(op => op.name === p.name)).map(p => (
                      <button key={p.name} className="ba-setup-popular-btn" onClick={() => addOpponentPokemon(p)} title={p.name}>
                        <img src={getSpriteUrl(p.name)} alt={p.name} className="ba-setup-popular-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        <span>{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="ba-opp-search">
                <input type="text" placeholder="Search Pokemon..." value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  disabled={opponentPokemon.length >= 6} />
                {filteredPokemon.length > 0 && (
                  <div className="ba-opp-results">
                    {filteredPokemon.map(p => (
                      <div key={p.name} className="ba-opp-result" onMouseDown={e => e.preventDefault()} onClick={() => addOpponentPokemon(p)}>
                        <img src={getSpriteUrl(p.name)} alt="" className="ba-sprite-sm" onError={e => { e.target.style.display = 'none'; }} />
                        <span>{p.name}</span>
                        <div style={{ display: 'flex', gap: '0.15rem' }}>
                          {p.types.map(t => <TypeBadge key={t} type={t} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected opponent Pokemon */}
              <div className="ba-setup-opp-slots">
                {[0,1,2,3,4,5].map(i => {
                  const p = opponentPokemon[i];
                  return p ? (
                    <div key={p.name} className="ba-setup-opp-slot filled">
                      <img src={getSpriteUrl(p.name)} alt="" className="ba-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      <div>
                        <span className="ba-setup-preview-name">{p.name}</span>
                        <div style={{display:'flex', gap:'0.15rem'}}>
                          {p.types.map(t => <TypeBadge key={t} type={t} />)}
                        </div>
                      </div>
                      <button className="ba-opp-remove" onClick={() => removeOpponentPokemon(p.name)}>&times;</button>
                    </div>
                  ) : (
                    <div key={i} className="ba-setup-opp-slot empty">
                      <span>Slot {i + 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="ba-setup-footer">
            <button className="ba-analyze-big" onClick={analyze} disabled={!yourTeam || opponentPokemon.length === 0}>
              Analyze Matchup
            </button>
          </div>

          {history.length > 0 && (() => {
            const filtered = historyTeamFilter && yourTeam
              ? history.filter(m => m.yourTeamName === yourTeam.name || m.yourTeamId === yourTeam.id)
              : history;
            const filteredStats = {
              wins: filtered.filter(m => m.result === 'win' || m.result === 'forfeit-win').length,
              losses: filtered.filter(m => m.result === 'loss' || m.result === 'forfeit-loss').length,
            };
            const decided = filteredStats.wins + filteredStats.losses;
            const rate = decided > 0 ? Math.round((filteredStats.wins / decided) * 100) : 0;
            const shown = showAllHistory ? filtered.slice().reverse() : filtered.slice(-10).reverse();
            return (
              <div className="ba-setup-history">
                <div className="ba-setup-history-header">
                  <div className="ba-setup-history-title">Match History</div>
                  <div className="ba-setup-history-stats">
                    <span className="ba-hs-wins">{filteredStats.wins}W</span>
                    <span className="ba-hs-sep">–</span>
                    <span className="ba-hs-losses">{filteredStats.losses}L</span>
                    <span className="ba-hs-rate">· {rate}% win rate</span>
                  </div>
                  <div className="ba-setup-history-controls">
                    {yourTeam && (
                      <button
                        type="button"
                        className={`ba-hs-filter${historyTeamFilter ? ' active' : ''}`}
                        onClick={() => setHistoryTeamFilter(v => !v)}
                        title={historyTeamFilter ? 'Showing only this team' : 'Show only this team'}
                      >
                        {historyTeamFilter ? `Only ${yourTeam.name}` : 'All teams'}
                      </button>
                    )}
                  </div>
                </div>
                {filtered.length === 0 ? (
                  <div className="ba-setup-history-empty">No matches recorded for this team yet.</div>
                ) : (
                  <div className="ba-setup-history-list">
                    {shown.map((m, idx) => {
                      const isWin = m.result === 'win' || m.result === 'forfeit-win';
                      const isForfeit = m.result === 'forfeit-win' || m.result === 'forfeit-loss';
                      const oppTeam = (m.opponentTeam || []).map(p => p.name || p);
                      const oppBrought = m.opponentBrought || [];
                      const oppLeads = m.opponentLeads || [];
                      const oppFainted = m.opponentFainted || [];
                      const yourBrought = m.yourBrought || [];
                      const yourLeads = m.yourLeads || [];
                      const yourFainted = m.yourFainted || [];
                      return (
                        <div key={m.timestamp ?? idx} className={`ba-history-card ${isWin ? 'win' : 'loss'}${isForfeit ? ' forfeit' : ''}`}>
                          <div className="ba-history-result" title={m.result}>
                            <span className="ba-history-result-letter">{isWin ? 'W' : 'L'}</span>
                            {isForfeit && <span className="ba-history-result-sub">FF</span>}
                          </div>
                          <div className="ba-history-body">
                            <div className="ba-history-meta">
                              <span className="ba-history-team">{m.yourTeamName || 'Team'}</span>
                              {m.archetype && <span className="ba-history-archetype">{m.archetype}</span>}
                              {m.format && <span className="ba-history-format">{m.format}</span>}
                              <span className="ba-history-time">{formatRelTime(m.timestamp)}</span>
                            </div>
                            <div className="ba-history-matchup">
                              <div className="ba-history-row">
                                <span className="ba-history-label">You</span>
                                <div className="ba-history-sprites">
                                  {yourBrought.length === 0 && <span className="ba-history-empty">—</span>}
                                  {yourBrought.map(n => {
                                    const isLead = yourLeads.includes(n);
                                    const isFainted = yourFainted.includes(n);
                                    return (
                                      <div key={n} className={`ba-history-sprite-wrap${isLead ? ' lead' : ''}${isFainted ? ' fainted' : ''}`} title={`${n}${isLead ? ' (lead)' : ''}${isFainted ? ' · fainted' : ''}`}>
                                        <img src={getSpriteUrl(n)} alt={n} className="ba-history-sprite" onError={e => { e.target.style.display = 'none'; }} />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="ba-history-row">
                                <span className="ba-history-label">Vs</span>
                                <div className="ba-history-sprites">
                                  {oppTeam.length === 0 && <span className="ba-history-empty">—</span>}
                                  {oppTeam.map(n => {
                                    const brought = oppBrought.includes(n);
                                    const isLead = oppLeads.includes(n);
                                    const isFainted = oppFainted.includes(n);
                                    const cls = isLead ? 'lead' : brought ? 'brought' : 'bench';
                                    return (
                                      <div key={n} className={`ba-history-sprite-wrap ${cls}${isFainted ? ' fainted' : ''}`} title={`${n}${isLead ? ' (lead)' : brought ? ' (brought)' : ' (benched)'}${isFainted ? ' · fainted' : ''}`}>
                                        <img src={getSpriteUrl(n)} alt={n} className="ba-history-sprite" onError={e => { e.target.style.display = 'none'; }} />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                          {oppTeam.length > 0 && (
                            <button
                              type="button"
                              className="ba-history-rematch"
                              onClick={() => loadOppTeam(m.opponentTeam)}
                              title="Load this opponent for a rematch"
                              aria-label="Rematch"
                            >↻</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {filtered.length > 10 && (
                  <button
                    type="button"
                    className="ba-setup-history-more"
                    onClick={() => setShowAllHistory(v => !v)}
                  >
                    {showAllHistory ? 'Show recent only' : `Show all (${filtered.length})`}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      ) : (
        <>
      {/* ---- ANALYSIS PHASE (full width with top bar) ---- */}
      <div className="ba-analysis">
        {/* Matchup bar: your bring vs opponent predicted brings */}
        <div className="ba-matchup-bar">
          <button className="ba-back-btn" onClick={() => { setRecommendation(null); setBattleMode(false); }}>&larr; New</button>

          <div className="ba-matchup-side ba-matchup-yours">
            <span className="ba-matchup-side-label">Your Team</span>
            <div className="ba-matchup-picks">
              {(() => {
                const allRecs = [...(recommendation?.selected || []), ...(recommendation?.benched || [])];
                const maxScore = Math.max(...allRecs.map(r => r.score || 0), 1);
                return yourTeam?.pokemon.map(p => {
                  const rec = allRecs.find(r => r.name === p.name);
                  const isSelected = recommendation?.selected?.some(s => s.name === p.name);
                  const isLead = recommendation?.leadSuggestion?.leads?.includes(p.name);
                  const isBench = !isSelected;
                  const pct = rec ? Math.round((rec.score / maxScore) * 100) : 0;
                  const leadReason = isLead ? (recommendation?.leadSuggestion?.reason || '') : '';
                  const bringReasons = rec?.reasons ? rec.reasons.join('. ') : (rec?.reason || '');
                  // Score breakdown so the user can see what drove the ranking
                  // (e.g. "why was Rotom-Heat benched for Sneasler?").
                  const bd = rec?.breakdown;
                  const bdLine = bd
                    ? `Score ${Math.round(bd.total * 10) / 10}: Offense ${bd.offense >= 0 ? '+' : ''}${Math.round(bd.offense * 10) / 10}, Defense ${bd.defense >= 0 ? '+' : ''}${Math.round(bd.defense * 10) / 10}${bd.immunity ? `, Immunity +${Math.round(bd.immunity * 10) / 10}` : ''}${bd.utility ? `, Utility +${Math.round(bd.utility * 10) / 10}` : ''}${bd.ability ? `, Ability +${Math.round(bd.ability * 10) / 10}` : ''}`
                    : '';
                  const tooltip = isLead
                    ? [leadReason, bdLine].filter(Boolean).join(' · ')
                    : [bringReasons, bdLine].filter(Boolean).join(' · ');
                  const isFainted = isFaintedYou(p.name);
                  const isActive = battleMode && activeYours.includes(p.name);
                  const canMega = canMegaEvolve(p) && isMegaStone(p.item);
                  const isMega = isMegaYou(p.name);
                  // Active ability: mega overrides base; else the team's assigned
                  // ability; else fall back to most-used ability for this species.
                  const yourBaseAbility = p.ability || getDefaultAbility(p.name, battleFormat);
                  const yourAbility = isMega ? (getMegaAbility(p.name, p.item) || yourBaseAbility) : yourBaseAbility;
                  const yourAbilityDesc = yourAbility ? (ABILITIES[yourAbility] || '') : '';
                  const yourAbilityInferred = !p.ability && !isMega;
                  const yourAbilityTooltip = yourAbility
                    ? (isMega
                        ? `${yourAbility} (Mega)${yourAbilityDesc ? ' — ' + yourAbilityDesc : ''}`
                        : `${yourAbility}${yourAbilityInferred ? ' (inferred — set in team builder)' : ''}${yourAbilityDesc ? ' — ' + yourAbilityDesc : ''}`)
                    : '';
                  return (
                    <div key={p.name} className="ba-pick-wrap">
                      {battleMode && (
                        <button className={`ba-above-btn${isFainted ? ' revive' : ''}`} onClick={() => toggleFainted(p.name)}>
                          {isFainted ? 'Revive' : 'Fainted'}
                        </button>
                      )}
                      <div
                        className={`ba-pick${!battleMode && isLead ? ' lead' : ''}${!battleMode && isBench ? ' bench' : ''}${isFainted ? ' fainted' : ''}${isActive ? ' active-field' : ''}${battleMode && !isActive && !isFainted ? ' bench-battle' : ''}${isMega ? ' mega-active' : ''}`}
                        data-tooltip={tooltip || ''}
                        onClick={battleMode && !isFainted && !isActive ? () => {
                          const maxActive = battleFormat === 'doubles' ? 2 : 1;
                          setActiveYours(prev => {
                            if (prev.length >= maxActive) return [...prev.slice(1), p.name];
                            return [...prev, p.name];
                          });
                        } : undefined}
                        style={battleMode && !isFainted && !isActive ? { cursor: 'pointer' } : undefined}
                      >
                        <img src={isMega ? getMegaSpriteUrl(p.name, p.item) : getSpriteUrl(p.name)} alt={p.name} className="ba-pick-sprite" onError={e => {
                          if (isMega && e.target.src !== getSpriteUrl(p.name)) { e.target.src = getSpriteUrl(p.name); return; }
                          e.target.style.display = 'none';
                        }} />
                        {p.item && (
                          <img
                            src={getItemSpriteUrl(p.item)}
                            alt={p.item}
                            title={p.item}
                            className="ba-pick-item"
                            onError={e => handleItemIconError(e, p.item)}
                          />
                        )}
                        <span className="ba-pick-name">{p.name}</span>
                        <span className="ba-pick-pct">{pct}%</span>
                        {battleMode && isActive && <span className="ba-pick-tag lead-tag">Active</span>}
                        {isLead && <span className="ba-pick-tag lead-tag">Lead</span>}
                        {isSelected && !isLead && <span className="ba-pick-tag back-tag">Back</span>}
                        {isBench && <span className="ba-pick-tag bench-tag">Bench</span>}
                        {yourAbility && (
                          <span
                            className={`ba-pick-ability${isMega ? ' mega' : ''}${yourAbilityInferred ? ' inferred' : ''}`}
                            data-tooltip={yourAbilityTooltip}
                          >
                            <span className="ba-pick-ability-text">{yourAbility}</span>
                          </span>
                        )}
                        {battleMode && canMega && !isFainted && (
                          <button
                            type="button"
                            className={`ba-mega-btn${isMega ? ' active' : ''}`}
                            onClick={e => { e.stopPropagation(); toggleMega(p.name, false); }}
                            title={isMega ? 'Revert Mega Evolution' : 'Mega Evolve'}
                          >
                            {isMega ? '✦ Mega' : 'Mega'}
                          </button>
                        )}
                      </div>
                      {battleMode && !isActive && !isFainted && (
                        <button className="ba-below-btn" onClick={() => {
                          const maxActive = battleFormat === 'doubles' ? 2 : 1;
                          setActiveYours(prev => {
                            if (prev.length >= maxActive) return [...prev.slice(1), p.name];
                            return [...prev, p.name];
                          });
                        }}>Swap in</button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          <span className="ba-matchup-vs">VS</span>

          <div className="ba-matchup-side ba-matchup-theirs">
            <span className="ba-matchup-side-label">Opponent</span>
            <div className="ba-matchup-picks">
              {opponentPokemon.map(p => {
                const preds = recommendation?.opponentPrediction?.predictions || [];
                const pred = preds.find(pr => pr.name === p.name);
                const isLikely = pred?.likely;
                const isLead = recommendation?.opponentPrediction?.predictedLeads?.[0]?.leads?.includes(p.name);
                const pct = pred?.probability || 0;
                const reasons = pred?.reasons?.join('. ') || '';
                const leadReasons = isLead ? (recommendation?.opponentPrediction?.predictedLeads?.[0]?.reasons?.join('. ') || '') : '';
                const tooltip = isLead ? (leadReasons || reasons) : reasons;
                const isFainted = isFaintedOpp(p.name);
                const isActive = battleMode && activeTheirs.includes(p.name);
                const isConfirmedBrought = confirmedOppBrought.includes(p.name);
                const isConfirmedLead = confirmedOppLeads.includes(p.name);
                const oppCanMega = canMegaEvolve(p);
                const oppIsMega = isMegaOpp(p.name);
                // Expected opponent ability: mega overrides, else most-used in this format.
                const oppBaseAbility = getDefaultAbility(p.name, battleFormat);
                const oppAbility = oppIsMega ? (getMegaAbility(p.name, p.item) || oppBaseAbility) : oppBaseAbility;
                const oppAbilityDesc = oppAbility ? (ABILITIES[oppAbility] || '') : '';
                const oppAbilityUsagePct = (() => {
                  if (!oppAbility || oppIsMega) return null;
                  const u = getAbilityUsage(p.name);
                  const pct = u?.[battleFormat]?.[oppAbility];
                  return pct != null ? pct : null;
                })();
                const oppAbilityTooltip = oppAbility
                  ? (oppIsMega
                      ? `${oppAbility} (Mega)${oppAbilityDesc ? ' — ' + oppAbilityDesc : ''}`
                      : `${oppAbility}${oppAbilityUsagePct != null ? ` — expected (${oppAbilityUsagePct}% in ${battleFormat})` : ' — expected'}${oppAbilityDesc ? '. ' + oppAbilityDesc : ''}`)
                  : '';

                // Click cycles: Unknown → Brought → Led → Unknown
                const cycleOppState = () => {
                  if (!isConfirmedBrought) { toggleOppConfirmedBrought(p.name); }
                  else if (!isConfirmedLead) { toggleOppConfirmedLead(p.name); }
                  else { toggleOppConfirmedLead(p.name); toggleOppConfirmedBrought(p.name); }
                };

                // Battle mode state label
                const stateLabel = isConfirmedLead ? 'Led' : isConfirmedBrought ? 'Brought' : isLikely ? `${pct}% likely` : 'Unknown';
                const stateClass = isConfirmedLead ? 'led' : isConfirmedBrought ? 'brought' : '';

                return (
                  <div key={p.name} className="ba-pick-wrap">
                    {battleMode && (
                      <button className={`ba-above-btn${isFainted ? ' revive' : ''}`} onClick={() => toggleFainted(p.name, true)}>
                        {isFainted ? 'Revive' : 'Fainted'}
                      </button>
                    )}
                    <div
                      className={`ba-pick opp${!battleMode && isLead ? ' lead' : ''}${!battleMode && !isLikely ? ' bench' : ''}${isFainted ? ' fainted' : ''}${isActive ? ' active-field' : ''}${battleMode && isConfirmedLead ? ' confirmed-lead' : ''}${battleMode && !isActive && !isFainted && !isConfirmedBrought ? ' bench-battle' : ''}${oppIsMega ? ' mega-active' : ''}`}
                      data-tooltip={!battleMode ? (tooltip || '') : ''}
                      onClick={battleMode && !isFainted && !isActive ? () => {
                        const maxActive = battleFormat === 'doubles' ? 2 : 1;
                        setActiveTheirs(prev => {
                          if (prev.length >= maxActive) return [...prev.slice(1), p.name];
                          return [...prev, p.name];
                        });
                        // Bringing a Pokemon onto the field implies they were
                        // brought to the match — auto-record it (up to the cap).
                        setConfirmedOppBrought(prev => {
                          if (prev.includes(p.name)) return prev;
                          if (prev.length >= selectCount) return prev;
                          return [...prev, p.name];
                        });
                      } : undefined}
                      style={battleMode && !isFainted && !isActive ? { cursor: 'pointer' } : undefined}
                    >
                      <img src={oppIsMega ? getMegaSpriteUrl(p.name, p.item) : getSpriteUrl(p.name)} alt={p.name} className="ba-pick-sprite" onError={e => {
                        if (oppIsMega && e.target.src !== getSpriteUrl(p.name)) { e.target.src = getSpriteUrl(p.name); return; }
                        e.target.style.display = 'none';
                      }} />
                      <span className="ba-pick-name">{p.name}</span>
                      <span className="ba-pick-pct">{pct}%</span>
                      {battleMode && isActive && <span className="ba-pick-tag lead-tag">Active</span>}
                      {isLead && <span className="ba-pick-tag lead-tag">Lead</span>}
                      {isLikely && !isLead && <span className="ba-pick-tag back-tag">Bring</span>}
                      {!isLikely && <span className="ba-pick-tag bench-tag">Bench</span>}
                      {oppAbility && (
                        <span
                          className={`ba-pick-ability opp-expected${oppIsMega ? ' mega' : ''}`}
                          data-tooltip={oppAbilityTooltip}
                        >
                          <span className="ba-pick-ability-text">{oppAbility}</span>
                        </span>
                      )}
                      {battleMode && oppCanMega && !isFainted && (
                        <button
                          type="button"
                          className={`ba-mega-btn${oppIsMega ? ' active' : ''}`}
                          onClick={e => { e.stopPropagation(); toggleMega(p.name, true); }}
                          title={oppIsMega ? 'Revert Mega Evolution' : 'Mega Evolve'}
                        >
                          {oppIsMega ? '✦ Mega' : 'Mega'}
                        </button>
                      )}
                    </div>
                    {battleMode && (
                      <button className={`ba-below-btn opp-state ${stateClass}`} onClick={cycleOppState} title="Click to cycle: Unknown → Brought → Led">
                        {stateLabel}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Strategy Briefing — visible before battle */}
        {!battleMode && recommendation && (() => {
          const allRecs = [...(recommendation?.selected || []), ...(recommendation?.benched || [])];
          const leadNames = recommendation?.leadSuggestion?.leads || [];
          const leadReason = recommendation?.leadSuggestion?.reason || '';
          const preds = recommendation?.opponentPrediction?.predictions || [];
          const oppLeadNames = recommendation?.opponentPrediction?.predictedLeads?.[0]?.leads || [];
          const oppLeadReasons = recommendation?.opponentPrediction?.predictedLeads?.[0]?.reasons || [];

          return (
          <div className="ba-briefing">
            <div className="ba-briefing-launch">
              <div className="ba-briefing-launch-rail yours-rail" aria-hidden="true">
                <span className="ba-rail-dot" /><span className="ba-rail-line" /><span className="ba-rail-chev">◄</span>
              </div>
              <button className="ba-start-battle-btn" onClick={startBattle}>
                <span className="ba-start-battle-spark" aria-hidden="true" />
                <span className="ba-start-battle-label">Start Battle</span>
                <span className="ba-start-battle-bolt" aria-hidden="true">⚡</span>
              </button>
              <div className="ba-briefing-launch-rail theirs-rail" aria-hidden="true">
                <span className="ba-rail-chev">►</span><span className="ba-rail-line" /><span className="ba-rail-dot" />
              </div>
            </div>
            <div className="ba-briefing-cols">
              {/* Your strategy */}
              <div className="ba-briefing-col yours-col">
                <div className="ba-briefing-title yours"><span>Your Game Plan</span></div>
                <div className="ba-briefing-lead">
                  <span className="ba-briefing-label">Lead with</span>
                  <div className="ba-briefing-lead-mons">
                    {leadNames.map(n => (
                      <span key={n} className="ba-briefing-mon">
                        <img src={getSpriteUrl(n)} alt="" className="ba-briefing-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
                {leadReason && <div className="ba-briefing-reason">{leadReason}</div>}
                <div className="ba-briefing-picks">
                  {allRecs.map(rec => {
                    const isPicked = recommendation?.selected?.some(s => s.name === rec.name);
                    return (
                      <div key={rec.name} className={`ba-briefing-pick${isPicked ? '' : ' benched'}`}>
                        <img src={getSpriteUrl(rec.name)} alt="" className="ba-briefing-sprite-sm" onError={e => { e.target.style.display = 'none'; }} />
                        <span className="ba-briefing-pick-name">{rec.name}</span>
                        {rec.reasons && <span className="ba-briefing-pick-reason">{rec.reasons[0]}</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Your win path */}
                {wincon && wincon.yourWinConditions.length > 0 && (
                  <div className="ba-scout-section">
                    <div className="ba-scout-label success">Your Win Path</div>
                    {wincon.yourWinConditions.map((wc, i) => (
                      <div key={i} className="ba-scout-wincon">
                        <img src={getSpriteUrl(wc.pokemon)} alt="" className="ba-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        <div>
                          <span className="ba-scout-threat-name">{wc.pokemon}</span>
                          <span className="ba-scout-threat-desc">{wc.description}</span>
                          {wc.blockers?.length > 0 && (
                            <div className="ba-scout-blockers">
                              KO first: {wc.blockers.map(b => (
                                <span key={b} className="ba-scout-blocker">
                                  <img src={getSpriteUrl(b)} alt="" className="ba-sprite-xs" onError={e => { e.target.style.display = 'none'; }} />{b}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Keep Alive */}
                {wincon && wincon.mustProtect.length > 0 && (
                  <div className="ba-scout-priority protect">
                    <span className="ba-scout-priority-label">Keep Alive</span>
                    <div className="ba-scout-priority-mons">
                      {wincon.mustProtect.map(n => (
                        <span key={n} className="ba-scout-priority-mon">
                          <img src={getSpriteUrl(n)} alt="" className="ba-sprite-sm" onError={e => { e.target.style.display = 'none'; }} />{n}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Opponent prediction */}
              <div className="ba-briefing-col theirs-col">
                <div className="ba-briefing-title theirs">
                  <span>Opponent Prediction</span>
                  {arch && <span className={`ba-scout-arch conf-${arch.confidence}`}>{arch.archetype}</span>}
                </div>
                {arch && arch.signals.length > 0 && (
                  <div className="ba-briefing-signals">
                    {arch.signals.map((s, i) => <span key={i} className="ba-briefing-signal">{s}</span>)}
                  </div>
                )}
                <div className="ba-briefing-lead">
                  <span className="ba-briefing-label">Likely leads</span>
                  <div className="ba-briefing-lead-mons">
                    {oppLeadNames.map(n => (
                      <span key={n} className="ba-briefing-mon opp">
                        <img src={getSpriteUrl(n)} alt="" className="ba-briefing-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
                {oppLeadReasons.length > 0 && <div className="ba-briefing-reason">{oppLeadReasons[0]}</div>}
                <div className="ba-briefing-picks">
                  {preds.map(pred => (
                    <div key={pred.name} className={`ba-briefing-pick${pred.likely ? '' : ' benched'}`}>
                      <img src={getSpriteUrl(pred.name)} alt="" className="ba-briefing-sprite-sm" onError={e => { e.target.style.display = 'none'; }} />
                      <span className="ba-briefing-pick-name">{pred.name}</span>
                      <span className="ba-briefing-pick-pct">{pred.probability}%</span>
                      {pred.reasons?.length > 0 && <span className="ba-briefing-pick-reason">{pred.reasons[0]}</span>}
                    </div>
                  ))}
                </div>

                {/* Threats to Watch */}
                {wincon && wincon.opponentWinConditions.length > 0 && (
                  <div className="ba-scout-section">
                    <div className="ba-scout-label danger">Threats to Watch</div>
                    {wincon.opponentWinConditions.map((wc, i) => (
                      <div key={i} className="ba-scout-threat">
                        <img src={getSpriteUrl(wc.pokemon)} alt="" className="ba-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        <div>
                          <span className="ba-scout-threat-name">{wc.pokemon}</span>
                          <span className="ba-scout-threat-desc">{wc.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Priority KO */}
                {wincon && wincon.mustKill.length > 0 && (
                  <div className="ba-scout-priority kill">
                    <span className="ba-scout-priority-label">Priority KO</span>
                    <div className="ba-scout-priority-mons">
                      {wincon.mustKill.map(n => (
                        <span key={n} className="ba-scout-priority-mon">
                          <img src={getSpriteUrl(n)} alt="" className="ba-sprite-sm" onError={e => { e.target.style.display = 'none'; }} />{n}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* How to Beat This */}
                {arch && arch.counterAdvice.length > 0 && (
                  <div className="ba-scout-counters">
                    <div className="ba-scout-label info">How to Beat This</div>
                    {arch.counterAdvice.map((a, i) => (
                      <div key={i} className="ba-scout-counter">{a}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}
        {battleMode && (
          <div className="ba-battle-controls">
            <div className="ba-battle-status">
              <span className="ba-battle-info">Brought: {confirmedOppBrought.length}/{selectCount}</span>
              <span className="ba-battle-info">Leads: {confirmedOppLeads.length}/{battleFormat === 'doubles' ? 2 : 1}</span>
            </div>

            {!matchResult && !endGamePrompt && (
              <div className="ba-battle-end">
                <button className="ba-end-btn win" onClick={() => tryEndBattle('win')}>Won</button>
                <button className="ba-end-btn loss" onClick={() => tryEndBattle('loss')}>Lost</button>
              </div>
            )}

            {!matchResult && endGamePrompt && (() => {
              const yourFaintedCount = (yourTeam?.pokemon || []).filter(p => isFaintedYou(p.name)).length;
              const oppFaintedCount = opponentPokemon.filter(p => isFaintedOpp(p.name)).length;
              const isLoss = endGamePrompt === 'loss';
              const need = isLoss
                ? `Mark ${selectCount} of your Pokemon as fainted (${yourFaintedCount}/${selectCount} so far) — or log it as a forfeit below.`
                : `Mark ${selectCount} of the opponent's Pokemon as fainted (${oppFaintedCount}/${selectCount} so far) — or log it as a forfeit below.`;
              return (
              <div className="ba-battle-prompt">
                <div className="ba-prompt-msg">
                  <span className="ba-prompt-warn">{need}</span>
                </div>
                <div className="ba-prompt-actions">
                  <button className="ba-prompt-btn save" onClick={() => finishBattle(endGamePrompt)}>Save Anyway</button>
                  <button className="ba-prompt-btn forfeit" onClick={() => finishBattle(endGamePrompt.includes('win') ? 'forfeit-win' : 'forfeit-loss')}>
                    {endGamePrompt === 'win' ? 'They Forfeited' : 'I Forfeited'}
                  </button>
                  <button className="ba-prompt-btn cancel" onClick={() => setEndGamePrompt(null)}>Go Back</button>
                </div>
              </div>
              );
            })()}

            {matchResult && (
              <div className="ba-battle-end">
                <span className={`ba-match-result ${matchResult}`}>
                  {matchResult === 'win' ? 'Victory!' : matchResult === 'loss' ? 'Defeat' : matchResult === 'forfeit-win' ? 'Won (Forfeit)' : 'Lost (Forfeit)'}
                  {' — Match saved'}
                </span>
              </div>
            )}
          </div>
        )}
        {/* Battlefield — active Pokemon face off */}
        {battleMode && activeYours.some(n => !isFaintedYou(n)) && activeTheirs.some(n => !isFaintedOpp(n)) && (() => {
          // Filter out fainted actives so they disappear from the battlefield
          // (supports 1v2 scenarios in doubles once a mon KOs).
          const liveYours = activeYours.filter(n => !isFaintedYou(n));
          const liveTheirs = activeTheirs.filter(n => !isFaintedOpp(n));

          // Get turn 1 move suggestions from decision tree
          const turn1 = dt?.turns?.[0];
          const topScenario = turn1?.scenarios?.[0];
          const moveSuggestions = {};
          if (topScenario?.response) {
            topScenario.response.forEach(r => {
              if (r.pokemon && r.pokemon !== 'General') moveSuggestions[r.pokemon] = r.action;
            });
          }

          // Get enriched opponent data for damage calcs
          const enrichedOpp = opponentPokemon.map(p => enrichOpponent(p, battleFormat));

          // Gather tactical notes for the sidebar panels
          const yourNotes = [];
          const oppNotes = [];

          // Your active Pokemon analysis
          liveYours.forEach(name => {
            const p = yourTeam?.pokemon?.find(pk => pk.name === name);
            if (!p) return;
            const ability = p.ability || '';
            const item = p.item || '';
            const holdsMegaStone = isMegaStone(item);

            // Mega evolution — only if actually holding a mega stone
            if (holdsMegaStone && canMegaEvolve(p)) {
              const megaAb = getMegaAbility(name, item);
              const preservesShield =
                (ability === 'Multiscale' || ability === 'Shadow Shield') &&
                (megaAb === 'Multiscale' || megaAb === 'Shadow Shield');
              if ((ability === 'Multiscale' || ability === 'Shadow Shield') && !preservesShield) {
                yourNotes.push({ type: 'info', text: `${name}: Delay Mega — ${ability} halves first hit. Take a hit first, then Mega next turn.` });
              } else if (ability === 'Intimidate') {
                yourNotes.push({ type: 'tip', text: `${name}: Intimidate already triggered. Mega Evolve now for the stat boost.` });
              } else if (preservesShield) {
                yourNotes.push({ type: 'tip', text: `${name}: Mega keeps ${ability} — Mega Evolve freely for the stat boost (still halves first hit at full HP).` });
              } else {
                yourNotes.push({ type: 'tip', text: `${name}: Mega Evolve for boosted stats (holding ${item})` });
              }
            }

            // Ability-specific advice
            if (ability === 'Multiscale' && !holdsMegaStone) {
              yourNotes.push({ type: 'tip', text: `${name}: Multiscale active at full HP — halves incoming damage. Don't switch out carelessly.` });
            }
            if (ability === 'Competitive') {
              const oppHasIntimidate = liveTheirs.some(n => {
                const op = enrichedOpp.find(o => o.name === n);
                return op?.ability === 'Intimidate';
              });
              if (oppHasIntimidate) yourNotes.push({ type: 'tip', text: `${name}: Competitive will boost SpA +2 from their Intimidate!` });
            }
            if (ability === 'Defiant') {
              const oppHasIntimidate = liveTheirs.some(n => {
                const op = enrichedOpp.find(o => o.name === n);
                return op?.ability === 'Intimidate';
              });
              if (oppHasIntimidate) yourNotes.push({ type: 'tip', text: `${name}: Defiant will boost Atk +2 from their Intimidate!` });
            }

            // Item-specific advice
            if (item === 'Focus Sash') yourNotes.push({ type: 'info', text: `${name}: Focus Sash survives any OHKO at full HP` });
            if (item === 'Choice Scarf') yourNotes.push({ type: 'info', text: `${name}: Choice Scarf — locked into one move but +50% Speed` });
          });

          // Pull tips and warnings from commentary
          if (commentary) {
            commentary.warnings.forEach(w => oppNotes.push({ type: 'warn', text: w }));
            commentary.tips.forEach(tip => {
              if (tip.type === 'warning') oppNotes.push({ type: 'warn', text: tip.text });
              else if (tip.type === 'tip') yourNotes.push({ type: 'tip', text: tip.text });
              else yourNotes.push({ type: 'info', text: tip.text });
            });
          }

          // Check for switch suggestions from scenarios
          if (turn1?.scenarios) {
            turn1.scenarios.forEach(s => {
              if (s.label?.includes('Switch') || s.note?.includes('switch')) {
                yourNotes.push({ type: 'warn', text: s.note || s.label });
              }
            });
          }

          // Opponent active Pokemon analysis
          liveTheirs.forEach(name => {
            const p = enrichedOpp.find(op => op.name === name);
            if (!p) return;
            const ab = p.ability || '';
            const item = p.item || '';

            // Entry abilities
            if (ab === 'Intimidate') oppNotes.push({ type: 'warn', text: `${name}: Intimidate — your physical attackers deal less damage` });
            if (ab === 'Drought') oppNotes.push({ type: 'info', text: `${name}: Drought → Sun active. Fire +50%, Water -50%` });
            if (ab === 'Drizzle') oppNotes.push({ type: 'info', text: `${name}: Drizzle → Rain active. Water +50%, Fire -50%` });
            if (ab === 'Sand Stream') oppNotes.push({ type: 'info', text: `${name}: Sand Stream → Sandstorm. Chip damage + SpD boost for Rock types` });
            if (ab === 'Snow Warning') oppNotes.push({ type: 'info', text: `${name}: Snow Warning → Snow. Blizzard never misses, Ice Def +50%` });

            // Defensive abilities
            if (ab === 'Magic Bounce') oppNotes.push({ type: 'warn', text: `${name}: Magic Bounce — status moves bounce back! Don't Taunt or Will-O-Wisp.` });
            if (ab === 'Prankster') oppNotes.push({ type: 'warn', text: `${name}: Prankster — priority Tailwind/Taunt/Thunder Wave incoming` });
            if (ab === 'Multiscale') oppNotes.push({ type: 'warn', text: `${name}: Multiscale — halved damage at full HP. Chip first, then nuke.` });
            if (ab === 'Disguise') oppNotes.push({ type: 'warn', text: `${name}: Disguise — first hit blocked. Don't waste your strongest move.` });
            if (ab === 'Unaware') oppNotes.push({ type: 'warn', text: `${name}: Unaware — ignores your stat boosts. Don't bother setting up.` });
            if (ab === 'Flash Fire') oppNotes.push({ type: 'warn', text: `${name}: Flash Fire — immune to Fire moves. Don't use them.` });
            if (ab === 'Water Absorb' || ab === 'Dry Skin') oppNotes.push({ type: 'warn', text: `${name}: ${ab} — absorbs Water moves. Avoid Water attacks.` });
            if (ab === 'Levitate') oppNotes.push({ type: 'warn', text: `${name}: Levitate — immune to Ground. Earthquake won't hit.` });

            // Item warnings
            if (item === 'Focus Sash') oppNotes.push({ type: 'info', text: `${name}: Likely Focus Sash — can't OHKO from full HP. Use multi-hit or chip.` });
            if (item === 'Choice Scarf') oppNotes.push({ type: 'info', text: `${name}: Predicted Choice Scarf — outspeeds but locked into one move.` });
          });

          return (
          <div className="ba-battlefield-wrap">
            {/* Your tactical notes */}
            <div className="ba-field-notes ba-field-notes-left">
              {yourNotes.slice(0, 4).map((n, i) => (
                <div key={i} className={`ba-field-note ${n.type}`}>{n.text}</div>
              ))}
            </div>

          <div className={`ba-battlefield${battleFormat === 'doubles' ? ' ba-battlefield-doubles' : ''}`}>
            {/* Your side: moves on left, sprites on right */}
            <div className="ba-field-side ba-field-yours">
              {liveYours.map((name, i) => {
                const p = yourTeam?.pokemon?.find(pk => pk.name === name);
                if (!p) return null;
                const attackerBase = { ...p, baseStats: p.baseStats || getBaseStats(p.name) };
                const attacker = withMega(attackerBase, isMegaYou(name));
                const myBoosts = stageBoosts[name] || {};
                // Fainted teammates on YOUR side (excludes the active attacker)
                const yourFaintedAllies = (yourTeam?.pokemon || []).filter(pk => pk.name !== name && isFaintedYou(pk.name)).length;

                // Calc every move against every active opponent, keep the best target
                const myMoves = (p.moves || []).map(m => {
                  const mt = MOVE_TYPES[m];
                  let bestHit = null;
                  let bestTarget = null;
                  liveTheirs.forEach(defName => {
                    const rawDef = enrichedOpp.find(op => op.name === defName);
                    if (!rawDef) return;
                    const defender = withMega(rawDef, isMegaOpp(defName));
                    const defBoosts = stageBoosts[defName] || {};
                    const result = estimateDamage(attacker, m, defender, { attackerBoosts: myBoosts, defenderBoosts: defBoosts, faintedAllies: yourFaintedAllies });
                    if (result && (!bestHit || result.maxPct > bestHit.maxPct)) {
                      bestHit = result;
                      bestTarget = defName;
                    }
                  });
                  return { name: m, type: mt, hit: bestHit, target: bestTarget };
                });

                // Find the optimal move (highest damage that can KO)
                const optimal = [...myMoves].filter(m => m.hit).sort((a, b) => {
                  // Prefer OHKOs, then 2HKOs, then highest %
                  const hkoRank = (h) => h === 'OHKO' ? 3 : h === '2HKO' ? 2 : h === '3HKO' ? 1 : 0;
                  const diff = hkoRank(b.hit.hko) - hkoRank(a.hit.hko);
                  return diff !== 0 ? diff : b.hit.maxPct - a.hit.maxPct;
                })[0];
                const optimalName = optimal?.name;

                // Sort: optimal first, then by damage
                myMoves.sort((a, b) => {
                  if (a.name === optimalName) return -1;
                  if (b.name === optimalName) return 1;
                  return (b.hit?.maxPct || 0) - (a.hit?.maxPct || 0);
                });

                return (
                  <div key={name} className="ba-field-unit" style={{ animationDelay: `${i * 0.15}s` }}>
                    <div className="ba-field-action-left">
                      <div className="ba-field-movelist">
                        {myMoves.map(m => {
                          const md = MOVE_DATA[m.name];
                          const cat = md?.cat;
                          const power = md?.power;
                          const tipParts = [m.type ? `${m.type}-type` : 'Status', cat, power && `${power} BP`, md?.spread && 'spread', md?.priority && `+${md.priority} priority`].filter(Boolean);
                          return (
                            <div key={m.name} className={`ba-field-moverow${m.name === optimalName ? ' recommended' : ''}`} data-tooltip={tipParts.join(' · ')} style={m.type ? { background: (TYPE_COLORS[m.type] || '#888') + '10' } : undefined}>
                              {m.type && <img src={getTypeIconUrl(m.type)} alt="" className="ba-field-moveicon" />}
                              <span className="ba-field-movename">{m.name}</span>
                              {m.hit ? (
                                <>
                                  <span className="ba-field-movetarget">→{m.target}</span>
                                  <span className={`ba-field-movedmg ${m.hit.hko === 'OHKO' ? 'ohko' : m.hit.hko === '2HKO' ? 'x2hko' : ''}`}>
                                    {m.hit.minPct}-{m.hit.maxPct}%
                                  </span>
                                </>
                              ) : (
                                <span className="ba-field-movedmg status">—</span>
                              )}
                              {m.name === optimalName && <span className="ba-field-best">BEST</span>}
                            </div>
                          );
                        })}
                      </div>
                      <StageControls name={name} boosts={myBoosts} onAdjust={adjustBoost} onReset={resetBoosts} />
                    </div>
                    <div className="ba-field-mon">
                      <img src={isMegaYou(name) ? getMegaSpriteUrl(name, p.item) : getSpriteUrl(name)} alt={name} className="ba-field-sprite ba-field-animate" onError={e => {
                        if (isMegaYou(name) && e.target.src !== getSpriteUrl(name)) { e.target.src = getSpriteUrl(name); return; }
                        e.target.style.display = 'none';
                      }} />
                      <span className="ba-field-name">{name}</span>
                      <div className="ba-field-types">{attacker.types.map(t => <TypeBadge key={t} type={t} />)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="ba-field-vs">
              <span>VS</span>
            </div>
            {/* Opponent side: sprites on left, predicted moves on right */}
            <div className="ba-field-side ba-field-theirs">
              {liveTheirs.map((name, i) => {
                const rawP = opponentPokemon.find(pk => pk.name === name);
                if (!rawP) return null;
                const baseP = enrichOpponent(rawP, battleFormat);
                const p = withMega(baseP, isMegaOpp(name));
                const oppBoosts = stageBoosts[name] || {};
                // Fainted teammates on OPPONENT side (excludes the active opponent)
                const oppFaintedAllies = opponentPokemon.filter(pk => pk.name !== name && isFaintedOpp(pk.name)).length;

                // Calc every predicted move against every active yours
                const oppMoves = (p.moves || []).map(m => {
                  const mt = MOVE_TYPES[m];
                  let bestHit = null;
                  let bestTarget = null;
                  liveYours.forEach(defName => {
                    const defender = yourTeam?.pokemon?.find(pk => pk.name === defName);
                    if (!defender) return;
                    const defWithStats = withMega({ ...defender, baseStats: defender.baseStats || getBaseStats(defender.name) }, isMegaYou(defName));
                    const defBoosts = stageBoosts[defName] || {};
                    const result = estimateDamage(p, m, defWithStats, { attackerBoosts: oppBoosts, defenderBoosts: defBoosts, faintedAllies: oppFaintedAllies });
                    if (result && (!bestHit || result.maxPct > bestHit.maxPct)) {
                      bestHit = result;
                      bestTarget = defName;
                    }
                  });
                  return { name: m, type: mt, hit: bestHit, target: bestTarget };
                });

                // Find their most dangerous move
                const mostDangerous = [...oppMoves].filter(m => m.hit).sort((a, b) => {
                  const hkoRank = (h) => h === 'OHKO' ? 3 : h === '2HKO' ? 2 : h === '3HKO' ? 1 : 0;
                  const diff = hkoRank(b.hit.hko) - hkoRank(a.hit.hko);
                  return diff !== 0 ? diff : b.hit.maxPct - a.hit.maxPct;
                })[0];
                const dangerName = mostDangerous?.name;

                oppMoves.sort((a, b) => (b.hit?.maxPct || 0) - (a.hit?.maxPct || 0));

                return (
                  <div key={name} className="ba-field-unit" style={{ animationDelay: `${i * 0.15 + 0.1}s` }}>
                    <div className="ba-field-mon">
                      <img src={isMegaOpp(name) ? getMegaSpriteUrl(name, p.item) : getSpriteUrl(name)} alt={name} className="ba-field-sprite ba-field-animate" onError={e => {
                        if (isMegaOpp(name) && e.target.src !== getSpriteUrl(name)) { e.target.src = getSpriteUrl(name); return; }
                        e.target.style.display = 'none';
                      }} />
                      <span className="ba-field-name">{name}</span>
                      <div className="ba-field-types">{p.types.map(t => <TypeBadge key={t} type={t} />)}</div>
                    </div>
                    <div className="ba-field-action-right">
                      <div className="ba-field-movelist opp">
                        {oppMoves.map(m => {
                          const md = MOVE_DATA[m.name];
                          const cat = md?.cat;
                          const power = md?.power;
                          const tipParts = [m.type ? `${m.type}-type` : 'Status', cat, power && `${power} BP`, md?.spread && 'spread', md?.priority && `+${md.priority} priority`].filter(Boolean);
                          return (
                            <div key={m.name} className={`ba-field-moverow${m.name === dangerName ? ' danger-move' : ''}`} data-tooltip={tipParts.join(' · ')} style={m.type ? { background: (TYPE_COLORS[m.type] || '#888') + '10' } : undefined}>
                              {m.type && <img src={getTypeIconUrl(m.type)} alt="" className="ba-field-moveicon" />}
                              <span className="ba-field-movename">{m.name}</span>
                              {m.hit ? (
                                <>
                                  <span className="ba-field-movetarget">→{m.target}</span>
                                  <span className={`ba-field-movedmg ${m.hit.hko === 'OHKO' ? 'ohko' : m.hit.hko === '2HKO' ? 'x2hko' : ''}`}>
                                    {m.hit.minPct}-{m.hit.maxPct}%
                                  </span>
                                </>
                              ) : (
                                <span className="ba-field-movedmg status">—</span>
                              )}
                              {m.name === dangerName && <span className="ba-field-danger">!</span>}
                            </div>
                          );
                        })}
                      </div>
                      <StageControls name={name} boosts={oppBoosts} onAdjust={adjustBoost} onReset={resetBoosts} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

            {/* Opponent tactical notes */}
            <div className="ba-field-notes ba-field-notes-right">
              {oppNotes.slice(0, 4).map((n, i) => (
                <div key={i} className={`ba-field-note ${n.type}`}>{n.text}</div>
              ))}
            </div>
          </div>
          );
        })()}

      {battleMode ? (
        <div className="ba-main ba-main-full">
          <div className="ba-content">
            {/* Live Battle Commentary — visible during battle */}
            {commentary && (commentary.tips.length > 0 || commentary.warnings.length > 0) && (
              <div className="ba-live-commentary">
                <h3>Live Coaching</h3>
                <div className="ba-live-tips">
                  {commentary.warnings.map((w, i) => (
                    <div key={`w${i}`} className="ba-live-tip warn">{w}</div>
                  ))}
                  {commentary.tips.map((tip, i) => (
                    <div key={`t${i}`} className={`ba-live-tip ${tip.type}`}>
                      <div className="ba-live-tip-text">{tip.text}</div>
                      {tip.advice && <div className="ba-live-tip-advice">{tip.advice}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Battle Plan — Bring / Lead / Watch-outs / Key Matchups */}
            {battlePlan && (battlePlan.bring?.length > 0 || battlePlan.lead || battlePlan.watchOuts?.length > 0) && (
              <div className="ba-battle-plan">
                <h3>Battle Plan</h3>

                {battlePlan.triggers?.length > 0 && (
                  <div className="ba-bp-section ba-bp-triggers">
                    <div className="ba-bp-section-head">Strategy triggers</div>
                    <ul className="ba-bp-list ba-bp-trigger-list">
                      {battlePlan.triggers.map((t, i) => (
                        <li key={i}>
                          <span className="ba-bp-trigger-match">{t.matchedOn}</span>
                          <span className="ba-bp-trigger-arrow">→</span>
                          <strong>{t.bring.join(' + ')}</strong>
                          {t.reason && <span className="ba-bp-trigger-reason"> — {t.reason}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {battlePlan.lead && (
                  <div className="ba-bp-section ba-bp-lead">
                    <div className="ba-bp-section-head">Lead: <strong>{battlePlan.lead.leads.join(' + ')}</strong></div>
                    {battlePlan.lead.why?.length > 0 && (
                      <ul className="ba-bp-list">
                        {battlePlan.lead.why.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    )}
                    {battlePlan.lead.avoid?.length > 0 && (
                      <ul className="ba-bp-list ba-bp-avoid">
                        {battlePlan.lead.avoid.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {battlePlan.bring?.length > 0 && (
                  <div className="ba-bp-section">
                    <div className="ba-bp-section-head">Bring</div>
                    <div className="ba-bp-bring-grid">
                      {battlePlan.bring.map((b, i) => (
                        <div key={i} className={`ba-bp-bring-card${b.triggered ? ' ba-bp-triggered' : ''}`}>
                          <div className="ba-bp-bring-head">
                            <img src={getSpriteUrl(b.name)} alt="" className="ba-sprite-xs" onError={e => { e.target.style.display = 'none'; }} />
                            <strong>{b.name}</strong>
                            {b.role && <span className="ba-bp-role">{b.role}</span>}
                          </div>
                          {b.why?.length > 0 && (
                            <ul className="ba-bp-list">
                              {b.why.map((r, j) => (
                                <li key={j} className={r.startsWith('[Trigger:') ? 'ba-bp-why-trigger' : ''}>{r}</li>
                              ))}
                            </ul>
                          )}
                          {b.scripts?.length > 0 && (
                            <div className="ba-bp-scripts">
                              {b.scripts.map((s, j) => (
                                <div key={j} className="ba-bp-script">
                                  <strong>vs {s.vs}:</strong> {s.script}
                                </div>
                              ))}
                            </div>
                          )}
                          {b.oneVOnes?.length > 0 && (
                            <ul className="ba-bp-list ba-bp-1v1s">
                              {b.oneVOnes.map((o, j) => (
                                <li key={j}><strong>vs {o.vs}:</strong> {o.note}</li>
                              ))}
                            </ul>
                          )}
                          {b.losesTo?.length > 0 && (
                            <ul className="ba-bp-list ba-bp-avoid">
                              {b.losesTo.map((r, j) => <li key={j}>{r}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {battlePlan.watchOuts?.length > 0 && (
                  <div className="ba-bp-section">
                    <div className="ba-bp-section-head">Watch-outs</div>
                    <ul className="ba-bp-list ba-bp-watchouts">
                      {battlePlan.watchOuts.map((w, i) => (
                        <li key={i} className={`ba-bp-watch ba-bp-sev-${w.severity}`}>
                          <span className="ba-bp-sev-tag">{w.severity}</span> {w.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {battlePlan.keyMatchups?.length > 0 && (
                  <div className="ba-bp-section">
                    <div className="ba-bp-section-head">Key matchups</div>
                    <ul className="ba-bp-list ba-bp-matchups">
                      {battlePlan.keyMatchups.map((m, i) => (
                        <li key={i}>
                          <img src={getSpriteUrl(m.threat)} alt="" className="ba-sprite-xs" onError={e => { e.target.style.display = 'none'; }} />
                          <strong>{m.threat}</strong> → <strong>{m.answer}</strong> {m.line}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
        ) : null}
      </div>
      </>
      )}
    </div>
  );
}

export default SelectionAdvisor;
